import { EditorTool, ViewerTool, MeasureTool } from '../../types/mapData';
import './Toolbar.css';

interface ToolbarProps {
  activeTool?: EditorTool | ViewerTool;
  onToolChange?: (tool: EditorTool | ViewerTool) => void;
  showGrid: boolean;
  onToggleGrid: () => void;
  viewOnly?: boolean;
  activeMeasureTool?: MeasureTool;
  onMeasureToolChange?: (tool: MeasureTool) => void;
}

interface ToolButton {
  id: EditorTool | ViewerTool;
  icon: string;
  label: string;
}

const EDITOR_TOOLS: ToolButton[] = [
  { id: 'pan', icon: 'fa-hand', label: 'Pan' },
  { id: 'brush', icon: 'fa-paintbrush', label: 'Brush' },
  { id: 'eraser', icon: 'fa-eraser', label: 'Eraser' },
  { id: 'prop', icon: 'fa-tree', label: 'Props' },
  { id: 'text', icon: 'fa-font', label: 'Text' },
  { id: 'pen', icon: 'fa-pen-nib', label: 'Pen' },
  { id: 'shape', icon: 'fa-shapes', label: 'Shapes' }
];

const VIEWER_TOOLS: ToolButton[] = [
  { id: 'pan', icon: 'fa-hand', label: 'Pan' }
];

interface MeasureToolButton {
  id: 'ruler' | 'tape';
  icon: string;
  label: string;
}

const MEASURE_TOOLS: MeasureToolButton[] = [
  { id: 'ruler', icon: 'fa-ruler', label: 'Ruler (straight line)' },
  { id: 'tape', icon: 'fa-drafting-compass', label: 'Tape measure (polyline)' }
];

/**
 * Toolbar component displaying available editor tools
 * @param activeTool - Currently selected tool
 * @param onToolChange - Callback when tool is selected
 * @param showGrid - Whether grid is visible
 * @param onToggleGrid - Callback to toggle grid visibility
 * @param viewOnly - If true, only show visual controls (grid toggle)
 * @param activeMeasureTool - Currently selected measure tool
 * @param onMeasureToolChange - Callback when measure tool is selected
 * @returns The rendered toolbar component
 */
function Toolbar({
  activeTool,
  onToolChange,
  showGrid,
  onToggleGrid,
  viewOnly = false,
  activeMeasureTool,
  onMeasureToolChange
}: ToolbarProps): JSX.Element {
  const handleMeasureToolClick = (toolId: 'ruler' | 'tape'): void => {
    if (!onMeasureToolChange) return;
    // Toggle off if clicking the same tool
    if (activeMeasureTool === toolId) {
      onMeasureToolChange(null);
    } else {
      onMeasureToolChange(toolId);
    }
  };

  const tools = viewOnly ? VIEWER_TOOLS : EDITOR_TOOLS;

  return (
    <div className="toolbar">
      {activeTool && onToolChange && (
        <>
          {tools.map((tool) => (
            <button
              key={tool.id}
              className={`toolbar-btn ${activeTool === tool.id && !activeMeasureTool ? 'toolbar-btn--active' : ''}`}
              onClick={() => {
                onToolChange(tool.id);
                onMeasureToolChange?.(null);
              }}
              title={tool.label}
            >
              <i className={`fas ${tool.icon}`}></i>
            </button>
          ))}
        </>
      )}
      {onMeasureToolChange && (
        <>
          {MEASURE_TOOLS.map((tool) => (
            <button
              key={tool.id}
              className={`toolbar-btn ${activeMeasureTool === tool.id ? 'toolbar-btn--active' : ''}`}
              onClick={() => handleMeasureToolClick(tool.id)}
              title={tool.label}
            >
              <i className={`fas ${tool.icon}`}></i>
            </button>
          ))}
        </>
      )}
      <button
        className={`toolbar-btn ${showGrid ? 'toolbar-btn--active' : ''}`}
        onClick={onToggleGrid}
        title={showGrid ? 'Hide grid' : 'Show grid'}
      >
        <i className="fas fa-th"></i>
      </button>
    </div>
  );
}

export default Toolbar;
